/**
 * Session handling (RULES §SEC-3): the short-lived access token lives in memory only; the
 * refresh token is an httpOnly cookie scoped to /api/auth that JavaScript never sees.
 *
 * `authFetch` is the fetch every API call goes through. On a 401 it refreshes the session
 * once (concurrent 401s share one refresh) and retries the request; when the refresh fails
 * it reports the session as lost, which sends the user to /login.
 */

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const tokenListeners = new Set<(token: string | null) => void>();
let authLostHandler: () => void = () => {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    const next = window.location.pathname + window.location.search;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }
};

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenListeners.forEach((fn) => fn(token));
}

export function onAccessTokenChange(fn: (token: string | null) => void): () => void {
  tokenListeners.add(fn);
  return () => tokenListeners.delete(fn);
}

/** Replace what happens when the session cannot be recovered (the router sets this). */
export function setAuthLostHandler(fn: () => void): void {
  authLostHandler = fn;
}

export function reportAuthLost(): void {
  setAccessToken(null);
  authLostHandler();
}

function apiUrl(path: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(path, origin).toString();
}

interface TokenOut {
  access_token: string;
  expires_in: number;
}

// The server remembers the company last switched to (a cookie), so a new tab continues there.
// Each tab keeps the company its own token acts in and asks for it on every refresh: switching
// in one tab must not move another tab's pages to a different company's data.
const ACTING_KEY = 'nova.actingCompany';
function actingCompany(): string | null {
  try {
    return sessionStorage.getItem(ACTING_KEY);
  } catch {
    return null;
  }
}
function keepActingCompany(token: string): void {
  try {
    const payload = JSON.parse(atob((token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/'))) as { cid?: string };
    if (payload.cid) sessionStorage.setItem(ACTING_KEY, payload.cid);
  } catch {
    /* private mode, or not a JWT: the server's cookie still applies */
  }
}

/**
 * Exchange the refresh cookie for a new access token. With `company`, the new token acts
 * in that company (the company switcher). Concurrent callers share one request.
 */
export function refreshSession(company?: string): Promise<boolean> {
  if (refreshing && !company) return refreshing;
  const acting = company ?? actingCompany();
  const query = acting ? `?company=${encodeURIComponent(acting)}` : '';
  const attempt = (async () => {
    try {
      const res = await globalThis.fetch(apiUrl(`/api/auth/refresh${query}`), {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        setAccessToken(null);
        return false;
      }
      const body = (await res.json()) as TokenOut;
      setAccessToken(body.access_token);
      keepActingCompany(body.access_token);
      return true;
    } catch {
      return false;
    }
  })();
  if (!company) {
    refreshing = attempt;
    void attempt.finally(() => {
      if (refreshing === attempt) refreshing = null;
    });
  }
  return attempt;
}

/** Make sure there is an access token, refreshing from the cookie if needed. */
export async function ensureSession(): Promise<boolean> {
  if (accessToken) return true;
  return refreshSession();
}

const AUTH_PATHS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout', '/api/auth/forgot', '/api/auth/reset'];

function isAuthEndpoint(url: string): boolean {
  const path = new URL(url, apiUrl('/')).pathname;
  return AUTH_PATHS.includes(path);
}

function withToken(request: Request): Request {
  if (accessToken) request.headers.set('Authorization', `Bearer ${accessToken}`);
  return request;
}

export async function authFetch(request: Request): Promise<Response> {
  const retry = request.clone();
  const res = await globalThis.fetch(withToken(request));
  if (res.status !== 401 || isAuthEndpoint(request.url)) return res;
  const ok = await refreshSession();
  if (!ok) {
    reportAuthLost();
    return res;
  }
  return globalThis.fetch(withToken(retry));
}

/** fetch() for a path, with the session (used for SSE streams and non-typed calls). */
export function sessionFetch(path: string, init?: RequestInit): Promise<Response> {
  return authFetch(new Request(apiUrl(path), { credentials: 'same-origin', ...init }));
}

export async function login(email: string, password: string): Promise<void> {
  try {
    sessionStorage.removeItem(ACTING_KEY);
  } catch {
    /* private mode */
  }
  const res = await globalThis.fetch(apiUrl('/api/auth/login'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(typeof body.detail === 'string' ? body.detail : `HTTP ${res.status}`);
  }
  const body = (await res.json()) as TokenOut;
  setAccessToken(body.access_token);
  keepActingCompany(body.access_token);
}

export async function logout(): Promise<void> {
  try {
    sessionStorage.removeItem(ACTING_KEY);
  } catch {
    /* private mode */
  }
  try {
    await globalThis.fetch(apiUrl('/api/auth/logout'), { method: 'POST', credentials: 'same-origin' });
  } finally {
    setAccessToken(null);
  }
}
